import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField,
  ToggleButton, ToggleButtonGroup, Alert, CircularProgress,
} from '@mui/material';
import PhoneField, { detectDefaultCountry, composeE164 } from './PhoneField.js';
import { ENDPOINTS, apiCall } from '../utils/api.js';

/**
 * Text/email a customer a "join the queue" link for a category (or facade). The link opens the
 * right queue with their phone prefilled. Reuses the shared PhoneField so the country handling
 * matches the login form.
 */
export default function SendInviteDialog({ open, orgId, category = null, facade = null, onClose }) {
  const [channel, setChannel] = useState('sms');
  const [country, setCountry] = useState(detectDefaultCountry());
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  const targetName = category?.name || facade?.name || 'the queue';

  const close = () => {
    setPhone(''); setEmail(''); setError(''); setSent(null); setBusy(false);
    onClose();
  };

  const send = async () => {
    setBusy(true); setError('');
    try {
      const body = { channel };
      if (channel === 'sms') body.phone = composeE164(country, phone);
      else body.email = email.trim();
      if (category?.id) body.category_id = category.id;
      if (facade?.id) body.facade_id = facade.id;
      const res = await apiCall(ENDPOINTS.SEND_INVITE(orgId), { method: 'POST', body: JSON.stringify(body) });
      setSent(res);
    } catch (e) {
      setError(e.message || 'Failed to send the link.');
    }
    setBusy(false);
  };

  const canSend = channel === 'sms' ? !!phone.trim() : !!email.trim();

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Send join link — {targetName}</DialogTitle>
      <DialogContent dividers>
        {sent ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Sent to <strong>{sent.sent_to}</strong> by {sent.channel === 'sms' ? 'text' : 'email'}.
            They can tap it to join {targetName}.
          </Alert>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>}
            <Typography variant="caption" color="text.secondary">
              We’ll {channel === 'sms' ? 'text' : 'email'} a link that opens “{targetName}” with the
              queue ready to join and their number pre-filled.
            </Typography>
            <ToggleButtonGroup
              exclusive value={channel} onChange={(_, v) => v && setChannel(v)}
              size="small" fullWidth sx={{ my: 1.5 }}
            >
              <ToggleButton value="sms">Text (SMS)</ToggleButton>
              <ToggleButton value="email">Email</ToggleButton>
            </ToggleButtonGroup>
            {channel === 'sms' ? (
              <PhoneField country={country} setCountry={setCountry} phone={phone} setPhone={setPhone}
                label="Customer phone" />
            ) : (
              <TextField fullWidth size="small" type="email" label="Customer email"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{sent ? 'Done' : 'Cancel'}</Button>
        {!sent && (
          <Button variant="contained" onClick={send} disabled={busy || !canSend} sx={{ borderRadius: 6 }}>
            {busy ? <CircularProgress size={18} /> : 'Send link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
